import type {
  StarMapData,
  StarMapFigure,
  StarMapConstellationInfo,
  StarMapConstellation,
  ConstellationWeight,
  CognitiveDimension,
  Territory,
  StuckLoop,
  ExpressedValue,
} from "@/lib/types"

// ---------------------------------------------------------------------------
// Figures — 2 per constellation, 32 total
// Constellation layout:
//   NE (Architects)  : 1 Logician, 2 Watchmaker, 3 Strategist, 4 Cartographer
//   NW (Seekers)     : 5 Mystic, 6 Explorer, 7 Rebel, 8 Alchemist
//   SE (Builders)    : 9 Guardian, 10 Architect, 11 Commander, 12 Healer
//   SW (Weavers)     : 13 Sage, 14 Nurturer, 15 Storyteller, 16 Bridge
// ---------------------------------------------------------------------------

const figures: StarMapFigure[] = [
  // --- NE: Architects ---
  // 1 Logician (Triangulum)
  { name: "Alan Turing", era: "1912-1954", constellation_id: 1, domain: "Architects", star_index: 0, local_x: 0.842, local_y: 0.796, magnitude: 3.41, real_star_name: "Mothallah", bayer: "α Tri" },
  { name: "Bertrand Russell", era: "1872-1970", constellation_id: 1, domain: "Architects", star_index: 1, local_x: 0.378, local_y: 0.080, magnitude: 3.00, real_star_name: "Deltotum", bayer: "β Tri" },
  // 2 Watchmaker (Lyra)
  { name: "Isaac Newton", era: "1643-1727", constellation_id: 2, domain: "Architects", star_index: 2, local_x: 0.665, local_y: 0.290, magnitude: 0.03, real_star_name: "Vega", bayer: "α Lyr" },
  { name: "Marie Curie", era: "1867-1934", constellation_id: 2, domain: "Architects", star_index: 3, local_x: 0.339, local_y: 0.737, magnitude: 3.24, real_star_name: "Sulafat", bayer: "γ Lyr" },
  // 3 Strategist (Draco)
  { name: "Sun Tzu", era: "544-496 BCE", constellation_id: 3, domain: "Architects", star_index: 4, local_x: 0.287, local_y: 0.674, magnitude: 2.23, real_star_name: "Eltanin", bayer: "γ Dra" },
  { name: "Niccolò Machiavelli", era: "1469-1527", constellation_id: 3, domain: "Architects", star_index: 5, local_x: 0.358, local_y: 0.657, magnitude: 2.79, real_star_name: "Rastaban", bayer: "β Dra" },
  // 4 Cartographer (Ursa Major)
  { name: "Charles Darwin", era: "1809-1882", constellation_id: 4, domain: "Architects", star_index: 6, local_x: 0.514, local_y: 0.384, magnitude: 1.79, real_star_name: "Dubhe", bayer: "α UMa" },
  { name: "Alexander von Humboldt", era: "1769-1859", constellation_id: 4, domain: "Architects", star_index: 7, local_x: 0.519, local_y: 0.484, magnitude: 2.37, real_star_name: "Merak", bayer: "β UMa" },

  // --- NW: Seekers ---
  // 5 Mystic (Cygnus)
  { name: "Rumi", era: "1207-1273", constellation_id: 5, domain: "Seekers", star_index: 8, local_x: 0.335, local_y: 0.347, magnitude: 1.25, real_star_name: "Deneb", bayer: "α Cyg" },
  { name: "Hildegard of Bingen", era: "1098-1179", constellation_id: 5, domain: "Seekers", star_index: 9, local_x: 0.455, local_y: 0.513, magnitude: 2.23, real_star_name: "Sadr", bayer: "γ Cyg" },
  // 6 Explorer (Orion)
  { name: "Richard Feynman", era: "1918-1988", constellation_id: 6, domain: "Seekers", star_index: 10, local_x: 0.718, local_y: 0.857, magnitude: 0.13, real_star_name: "Rigel", bayer: "β Ori" },
  { name: "Amelia Earhart", era: "1897-1937", constellation_id: 6, domain: "Seekers", star_index: 11, local_x: 0.282, local_y: 0.188, magnitude: 0.50, real_star_name: "Betelgeuse", bayer: "α Ori" },
  // 7 Rebel (Scorpius)
  { name: "Friedrich Nietzsche", era: "1844-1900", constellation_id: 7, domain: "Seekers", star_index: 12, local_x: 0.650, local_y: 0.320, magnitude: 0.96, real_star_name: "Antares", bayer: "α Sco" },
  { name: "Nikola Tesla", era: "1856-1943", constellation_id: 7, domain: "Seekers", star_index: 13, local_x: 0.154, local_y: 0.707, magnitude: 1.63, real_star_name: "Shaula", bayer: "λ Sco" },
  // 8 Alchemist (Phoenix — user's constellation)
  { name: "Leonardo da Vinci", era: "1452-1519", constellation_id: 8, domain: "Seekers", star_index: 14, local_x: 0.738, local_y: 0.148, magnitude: 2.38, real_star_name: "Ankaa", bayer: "α Phe" },
  { name: "Carl Jung", era: "1875-1961", constellation_id: 8, domain: "Seekers", star_index: 15, local_x: 0.342, local_y: 0.411, magnitude: 3.31, real_star_name: "β Phe", bayer: "β Phe" },

  // --- SE: Builders ---
  // 9 Guardian (Canis Major)
  { name: "Marcus Aurelius", era: "121-180", constellation_id: 9, domain: "Builders", star_index: 16, local_x: 0.593, local_y: 0.117, magnitude: -1.46, real_star_name: "Sirius", bayer: "α CMa" },
  { name: "Cicero", era: "106-43 BCE", constellation_id: 9, domain: "Builders", star_index: 17, local_x: 0.416, local_y: 0.820, magnitude: 1.50, real_star_name: "Adhara", bayer: "ε CMa" },
  // 10 Architect (Cepheus)
  { name: "Benjamin Franklin", era: "1706-1790", constellation_id: 10, domain: "Builders", star_index: 18, local_x: 0.718, local_y: 0.730, magnitude: 2.51, real_star_name: "Alderamin", bayer: "α Cep" },
  { name: "Thomas Jefferson", era: "1743-1826", constellation_id: 10, domain: "Builders", star_index: 19, local_x: 0.677, local_y: 0.386, magnitude: 3.23, real_star_name: "Alfirk", bayer: "β Cep" },
  // 11 Commander (Leo)
  { name: "Winston Churchill", era: "1874-1965", constellation_id: 11, domain: "Builders", star_index: 20, local_x: 0.767, local_y: 0.702, magnitude: 1.40, real_star_name: "Regulus", bayer: "α Leo" },
  { name: "Alexander the Great", era: "356-323 BCE", constellation_id: 11, domain: "Builders", star_index: 21, local_x: 0.080, local_y: 0.627, magnitude: 2.14, real_star_name: "Denebola", bayer: "β Leo" },
  // 12 Healer (Ophiuchus)
  { name: "Hippocrates", era: "c. 460-370 BCE", constellation_id: 12, domain: "Builders", star_index: 22, local_x: 0.343, local_y: 0.080, magnitude: 2.08, real_star_name: "Rasalhague", bayer: "α Oph" },
  { name: "Avicenna", era: "980-1037", constellation_id: 12, domain: "Builders", star_index: 23, local_x: 0.479, local_y: 0.713, magnitude: 2.43, real_star_name: "Sabik", bayer: "η Oph" },

  // --- SW: Weavers ---
  // 13 Sage (Centaurus)
  { name: "Socrates", era: "470-399 BCE", constellation_id: 13, domain: "Weavers", star_index: 24, local_x: 0.080, local_y: 0.911, magnitude: -0.27, real_star_name: "Rigil Kentaurus", bayer: "α Cen" },
  { name: "Carl Sagan", era: "1934-1996", constellation_id: 13, domain: "Weavers", star_index: 25, local_x: 0.279, local_y: 0.895, magnitude: 0.61, real_star_name: "Hadar", bayer: "β Cen" },
  // 14 Nurturer (Virgo)
  { name: "Mahatma Gandhi", era: "1869-1948", constellation_id: 14, domain: "Weavers", star_index: 26, local_x: 0.314, local_y: 0.784, magnitude: 0.98, real_star_name: "Spica", bayer: "α Vir" },
  { name: "Jane Goodall", era: "1934-", constellation_id: 14, domain: "Weavers", star_index: 27, local_x: 0.593, local_y: 0.535, magnitude: 2.74, real_star_name: "Porrima", bayer: "γ Vir" },
  // 15 Storyteller (Pegasus)
  { name: "William Shakespeare", era: "1564-1616", constellation_id: 15, domain: "Weavers", star_index: 28, local_x: 0.920, local_y: 0.698, magnitude: 2.39, real_star_name: "Enif", bayer: "ε Peg" },
  { name: "Homer", era: "c. 8th c. BCE", constellation_id: 15, domain: "Weavers", star_index: 29, local_x: 0.471, local_y: 0.266, magnitude: 2.42, real_star_name: "Scheat", bayer: "β Peg" },
  // 16 Bridge (Eridanus)
  { name: "Nelson Mandela", era: "1918-2013", constellation_id: 16, domain: "Weavers", star_index: 30, local_x: 0.862, local_y: 0.920, magnitude: 0.46, real_star_name: "Achernar", bayer: "α Eri" },
  { name: "Abraham Lincoln", era: "1809-1865", constellation_id: 16, domain: "Weavers", star_index: 31, local_x: 0.138, local_y: 0.080, magnitude: 2.80, real_star_name: "Cursa", bayer: "β Eri" },
]

// ---------------------------------------------------------------------------
// Edges — connect figures within the same constellation
// ---------------------------------------------------------------------------

const edges: [number, number][] = [
  // Logician
  [0, 1],
  // Watchmaker
  [2, 3],
  // Strategist
  [4, 5],
  // Cartographer
  [6, 7],
  // Mystic
  [8, 9],
  // Explorer
  [10, 11],
  // Rebel
  [12, 13],
  // Alchemist
  [14, 15],
  // Guardian
  [16, 17],
  // Architect
  [18, 19],
  // Commander
  [20, 21],
  // Healer
  [22, 23],
  // Sage
  [24, 25],
  // Nurturer
  [26, 27],
  // Storyteller
  [28, 29],
  // Bridge
  [30, 31],
]

// ---------------------------------------------------------------------------
// Constellation metadata (info array for the star map renderer)
// ---------------------------------------------------------------------------

const constellations: StarMapConstellationInfo[] = [
  { id: 1, name: "Logician", domain: "Architects", figure_indices: [0, 1], real_name: "Triangulum", quadrant: "NE", centroid_local_x: 0.610, centroid_local_y: 0.438 },
  { id: 2, name: "Watchmaker", domain: "Architects", figure_indices: [2, 3], real_name: "Lyra", quadrant: "NE", centroid_local_x: 0.502, centroid_local_y: 0.513 },
  { id: 3, name: "Strategist", domain: "Architects", figure_indices: [4, 5], real_name: "Draco", quadrant: "NE", centroid_local_x: 0.323, centroid_local_y: 0.665 },
  { id: 4, name: "Cartographer", domain: "Architects", figure_indices: [6, 7], real_name: "Ursa Major", quadrant: "NE", centroid_local_x: 0.516, centroid_local_y: 0.434 },
  { id: 5, name: "Mystic", domain: "Seekers", figure_indices: [8, 9], real_name: "Cygnus", quadrant: "NW", centroid_local_x: 0.395, centroid_local_y: 0.430 },
  { id: 6, name: "Explorer", domain: "Seekers", figure_indices: [10, 11], real_name: "Orion", quadrant: "NW", centroid_local_x: 0.500, centroid_local_y: 0.522 },
  { id: 7, name: "Rebel", domain: "Seekers", figure_indices: [12, 13], real_name: "Scorpius", quadrant: "NW", centroid_local_x: 0.402, centroid_local_y: 0.513 },
  { id: 8, name: "Alchemist", domain: "Seekers", figure_indices: [14, 15], real_name: "Phoenix", quadrant: "NW", centroid_local_x: 0.540, centroid_local_y: 0.279 },
  { id: 9, name: "Guardian", domain: "Builders", figure_indices: [16, 17], real_name: "Canis Major", quadrant: "SE", centroid_local_x: 0.504, centroid_local_y: 0.468 },
  { id: 10, name: "Architect", domain: "Builders", figure_indices: [18, 19], real_name: "Cepheus", quadrant: "SE", centroid_local_x: 0.698, centroid_local_y: 0.558 },
  { id: 11, name: "Commander", domain: "Builders", figure_indices: [20, 21], real_name: "Leo", quadrant: "SE", centroid_local_x: 0.423, centroid_local_y: 0.664 },
  { id: 12, name: "Healer", domain: "Builders", figure_indices: [22, 23], real_name: "Ophiuchus", quadrant: "SE", centroid_local_x: 0.411, centroid_local_y: 0.396 },
  { id: 13, name: "Sage", domain: "Weavers", figure_indices: [24, 25], real_name: "Centaurus", quadrant: "SW", centroid_local_x: 0.180, centroid_local_y: 0.903 },
  { id: 14, name: "Nurturer", domain: "Weavers", figure_indices: [26, 27], real_name: "Virgo", quadrant: "SW", centroid_local_x: 0.454, centroid_local_y: 0.659 },
  { id: 15, name: "Storyteller", domain: "Weavers", figure_indices: [28, 29], real_name: "Pegasus", quadrant: "SW", centroid_local_x: 0.696, centroid_local_y: 0.482 },
  { id: 16, name: "Bridge", domain: "Weavers", figure_indices: [30, 31], real_name: "Eridanus", quadrant: "SW", centroid_local_x: 0.500, centroid_local_y: 0.500 },
]

// ---------------------------------------------------------------------------
// Assigned constellation — Alchemist (id 8)
// ---------------------------------------------------------------------------

const assignedConstellation: StarMapConstellation = {
  id: 8,
  name: "Alchemist",
  domain: "Seekers",
  domain_descriptor: "Those who dissolve boundaries to forge new understanding",
  tagline: "Transmutes friction between domains into unexpected synthesis",
  shadow: "Scattered Catalyst",
  shadow_description: "Starts a hundred transformations, finishes few. Mistakes the spark of insight for the completed work.",
  mythological_root: "Hermes Trismegistus — the thrice-great mediator between worlds, who saw that to understand anything deeply you must hold opposites in the same hand.",
  axis_cognition: "synthetic",
  axis_orientation: "generative",
  real_name: "Phoenix",
  iau_abbrev: "Phe",
  primary_star: "Ankaa",
  quadrant: "NW",
}

// ---------------------------------------------------------------------------
// Constellation weights — user's affinity distribution across all 16
// ---------------------------------------------------------------------------

const constellationWeights: ConstellationWeight[] = [
  { id: 1, name: "Logician", domain: "Architects", weight: 0.04 },
  { id: 2, name: "Watchmaker", domain: "Architects", weight: 0.03 },
  { id: 3, name: "Strategist", domain: "Architects", weight: 0.06 },
  { id: 4, name: "Cartographer", domain: "Architects", weight: 0.09 },
  { id: 5, name: "Mystic", domain: "Seekers", weight: 0.07 },
  { id: 6, name: "Explorer", domain: "Seekers", weight: 0.08 },
  { id: 7, name: "Rebel", domain: "Seekers", weight: 0.11 },
  { id: 8, name: "Alchemist", domain: "Seekers", weight: 0.18 },
  { id: 9, name: "Guardian", domain: "Builders", weight: 0.02 },
  { id: 10, name: "Architect", domain: "Builders", weight: 0.05 },
  { id: 11, name: "Commander", domain: "Builders", weight: 0.03 },
  { id: 12, name: "Healer", domain: "Builders", weight: 0.06 },
  { id: 13, name: "Sage", domain: "Weavers", weight: 0.05 },
  { id: 14, name: "Nurturer", domain: "Weavers", weight: 0.03 },
  { id: 15, name: "Storyteller", domain: "Weavers", weight: 0.06 },
  { id: 16, name: "Bridge", domain: "Weavers", weight: 0.04 },
]

// ---------------------------------------------------------------------------
// SAMPLE_STAR_MAP — v2 format
// ---------------------------------------------------------------------------

export const SAMPLE_STAR_MAP: StarMapData = {
  version: 2,
  figures,
  edges,
  constellations,
  user_position: null,
  assigned_constellation: assignedConstellation,
  constellation_weights: constellationWeights,
  nearest_figures: [
    { name: "Leonardo da Vinci", similarity: 0.78, star_index: 14 },
    { name: "Carl Jung", similarity: 0.72, star_index: 15 },
    { name: "Nikola Tesla", similarity: 0.65, star_index: 13 },
    { name: "Nelson Mandela", similarity: 0.61, star_index: 30 },
    { name: "Benjamin Franklin", similarity: 0.58, star_index: 18 },
  ],
  share_text: {
    headline: "The Alchemist",
    subtitle: "Transmutes friction between domains into unexpected synthesis",
    nearest_star: "Nearest star: Leonardo da Vinci (78% similarity)",
    full_text: "My cognitive constellation is The Alchemist — nearest star: Leonardo da Vinci (78%). I transmute friction between domains into unexpected synthesis. Mapped by Lumensis.",
  },
}

// ---------------------------------------------------------------------------
// SAMPLE_DIMENSIONS — 8 cognitive dimensions
// ---------------------------------------------------------------------------

export const SAMPLE_DIMENSIONS: CognitiveDimension[] = [
  {
    name: "integrative_complexity",
    score: 0.82,
    confidence: 0.88,
    evidence_quote: "I think the real answer lives at the boundary between information theory and phenomenology — you can't reduce one to the other, but the interference pattern between them is where the interesting structure shows up.",
    evidence_observation: "Consistently holds multiple incompatible frameworks in parallel rather than collapsing to a single interpretation. Produces novel synthesis across 4+ domains within the same reasoning chain.",
  },
  {
    name: "abstraction_level",
    score: 0.74,
    confidence: 0.85,
    evidence_quote: "Every system has an implicit theory of time baked into it. The question isn't whether your architecture handles state — it's what temporal ontology it commits you to.",
    evidence_observation: "Operates primarily at the level of structural metaphors and second-order patterns. Moves fluidly between concrete instances and governing abstractions.",
  },
  {
    name: "epistemic_breadth",
    score: 0.79,
    confidence: 0.82,
    evidence_quote: "This is basically the same problem thermodynamics solved with entropy, except the boundary conditions are social rather than physical. Read Jaynes on MaxEnt — the math transfers directly.",
    evidence_observation: "References span 6+ distinct knowledge domains. Cross-domain transfer is not decorative — it carries genuine structural insight and appropriate caveats.",
  },
  {
    name: "systematic_rigor",
    score: 0.61,
    confidence: 0.79,
    evidence_quote: "I'm not sure this holds up under edge cases, but the intuition feels right enough to build on. We can formalize later once the shape is clearer.",
    evidence_observation: "Prioritizes generative exploration over formal verification. Acknowledges incompleteness but rarely backtracks to close open loops systematically.",
  },
  {
    name: "creative_latitude",
    score: 0.88,
    confidence: 0.91,
    evidence_quote: "What if consciousness is the residual error term in a self-model that can't quite predict itself? Not a feature — a bug that became load-bearing.",
    evidence_observation: "Generates unexpected conceptual combinations at high frequency. Reframes questions rather than answering them directly. Treats constraints as creative material.",
  },
  {
    name: "metacognitive_awareness",
    score: 0.76,
    confidence: 0.84,
    evidence_quote: "I notice I keep circling back to the same structural metaphor. That's probably telling me something about a blind spot in how I'm framing this.",
    evidence_observation: "Monitors own reasoning process in real-time. Flags potential biases and framing effects unprompted. Occasionally over-indexes on meta-level reflection at the cost of forward progress.",
  },
  {
    name: "agency",
    score: 0.71,
    confidence: 0.80,
    evidence_quote: "Alright, I think there's a real project here. Let me sketch out what the first iteration could look like — even if it's wrong, it gives us something concrete to push against.",
    evidence_observation: "Transitions from analysis to proposed action regularly, though with a characteristic delay. Prefers to build conceptual scaffolding before committing to implementation.",
  },
  {
    name: "emotional_register",
    score: 0.65,
    confidence: 0.77,
    evidence_quote: "There's something genuinely beautiful about the way this structure unfolds — it has the same quality as watching a proof that feels inevitable in hindsight.",
    evidence_observation: "Emotional engagement surfaces primarily through aesthetic and intellectual affect — wonder, elegance, frustration with incoherence. Relational and somatic registers are present but less frequent.",
  },
]

// ---------------------------------------------------------------------------
// SAMPLE_TERRITORIES — 8 territories with mixed aliveness
// ---------------------------------------------------------------------------

export const SAMPLE_TERRITORIES: Territory[] = [
  {
    id: 0,
    label: "Systems Architecture",
    description: "Structural design of complex adaptive systems, feedback loops, and emergent properties",
    centroid: [0.72, 0.34, -0.18],
    members: [0, 3, 7, 14, 22, 29, 31, 38],
    energy: 0.89,
    vitality: 0.84,
    aliveness: "alive",
  },
  {
    id: 1,
    label: "Creative Problem-Solving",
    description: "Lateral thinking, constraint reframing, and generative approaches to novel challenges",
    centroid: [0.45, 0.61, 0.22],
    members: [1, 8, 15, 23, 30, 36],
    energy: 0.81,
    vitality: 0.79,
    aliveness: "alive",
  },
  {
    id: 2,
    label: "Philosophy of Mind",
    description: "Consciousness, phenomenology, self-models, and the hard problem",
    centroid: [-0.31, 0.55, 0.47],
    members: [2, 9, 16, 24, 33],
    energy: 0.73,
    vitality: 0.71,
    aliveness: "alive",
  },
  {
    id: 3,
    label: "Information Theory",
    description: "Entropy, compression, signal-noise boundaries, and coding theory",
    centroid: [0.58, -0.22, -0.41],
    members: [4, 11, 18, 25],
    energy: 0.62,
    vitality: 0.55,
    aliveness: "routine",
  },
  {
    id: 4,
    label: "Embodied Cognition",
    description: "Sensorimotor grounding, environmental coupling, and enacted perception",
    centroid: [-0.15, 0.28, 0.63],
    members: [5, 12, 19, 26, 34],
    energy: 0.54,
    vitality: 0.48,
    aliveness: "routine",
  },
  {
    id: 5,
    label: "Ethics & Governance",
    description: "Moral reasoning, institutional design, and the politics of technical systems",
    centroid: [-0.48, -0.19, 0.35],
    members: [6, 13, 20, 27],
    energy: 0.41,
    vitality: 0.38,
    aliveness: "neutral",
  },
  {
    id: 6,
    label: "Mathematical Foundations",
    description: "Formal logic, proof theory, category theory, and foundational questions",
    centroid: [0.82, -0.45, -0.12],
    members: [10, 17, 28, 35],
    energy: 0.35,
    vitality: 0.29,
    aliveness: "strain",
  },
  {
    id: 7,
    label: "Personal Narrative",
    description: "Identity construction, autobiographical reflection, and meaning-making",
    centroid: [-0.62, 0.41, 0.15],
    members: [21, 32, 37],
    energy: 0.28,
    vitality: 0.22,
    aliveness: "strain",
  },
]

// ---------------------------------------------------------------------------
// SAMPLE_STUCK_LOOPS — recurring conceptual orbits
// ---------------------------------------------------------------------------

export const SAMPLE_STUCK_LOOPS: StuckLoop[] = [
  {
    topic: "The relationship between consciousness and computation",
    occurrences: 4,
    similarity: 0.87,
    extracted_questions: [
      { content: "Can a self-model that predicts itself ever be complete, or does Godel guarantee a residual?", timestamp: null, is_framing_shift: true },
      { content: "Is subjective experience an intrinsic property of certain information structures, or is it substrate-dependent?", timestamp: null, is_framing_shift: false },
      { content: "What would a non-computational theory of consciousness even look like — and would we recognize it as a theory?", timestamp: null, is_framing_shift: true },
    ],
  },
  {
    topic: "How to bridge formal systems and intuitive understanding",
    occurrences: 3,
    similarity: 0.79,
    extracted_questions: [
      { content: "Is formalization always a compression of understanding, or can it sometimes expand what's thinkable?", timestamp: null, is_framing_shift: false },
      { content: "Why do the most productive insights arrive as spatial metaphors before they become equations?", timestamp: null, is_framing_shift: true },
    ],
  },
]

// ---------------------------------------------------------------------------
// SAMPLE_VALUES — expressed cognitive values
// ---------------------------------------------------------------------------

export const SAMPLE_VALUES: ExpressedValue[] = [
  { name: "Intellectual Honesty", score: 0.91, evidence: "Repeatedly flags uncertainty and distinguishes between confident claims and speculative leaps, even when it weakens the argument being built." },
  { name: "Synthesis Over Analysis", score: 0.86, evidence: "Consistently seeks to unify disparate frameworks rather than decompose them. Treats integration as the primary cognitive act." },
  { name: "Aesthetic Elegance", score: 0.78, evidence: "Evaluates ideas partly on structural beauty — compactness, surprise, inevitability. Discards correct-but-ugly solutions in favor of ones that 'feel right.'" },
  { name: "Autonomy of Thought", score: 0.74, evidence: "Resists received framings and authority-based arguments. Rebuilds conclusions from first principles even when the standard answer is available." },
  { name: "Generative Risk-Taking", score: 0.69, evidence: "Willing to follow an uncertain line of reasoning to see where it leads. Treats dead ends as information rather than failure." },
  { name: "Ecological Awareness", score: 0.58, evidence: "Considers second-order effects and systemic consequences. Asks 'what does this change downstream?' before committing to a position." },
]

// ---------------------------------------------------------------------------
// SAMPLE_ARCHETYPE — landing page archetype card
// ---------------------------------------------------------------------------

export const SAMPLE_ARCHETYPE = {
  label: "The Alchemist",
  hook: "You don't solve problems — you transmute them. Where others see contradictions, you see raw material for synthesis.",
  constellation: "Alchemist",
  domain: "Seekers",
  narrative: "Your mind operates like a conceptual forge — pulling materials from distant domains and subjecting them to enough heat that they fuse into something genuinely new. You're drawn to the friction between frameworks, not because you enjoy conflict, but because that's where the interesting phase transitions happen. Your strongest work emerges when you stop trying to resolve a tension and start building with it instead.",
}

// ---------------------------------------------------------------------------
// SAMPLE_NEAREST_FIGURE — figure match card
// ---------------------------------------------------------------------------

export const SAMPLE_NEAREST_FIGURE = {
  name: "Leonardo da Vinci",
  similarity: 0.78,
  narrative: "Like da Vinci, you refuse to recognize the boundary between art and science, theory and practice, observation and invention. He dissected cadavers to paint better and painted to understand anatomy — each domain feeding the other in an endless spiral. Your cognitive signature shares that same polymathic restlessness: the conviction that the deepest understanding comes from holding a problem in multiple frames simultaneously until they fuse into something none of them could have produced alone.",
}

// ---------------------------------------------------------------------------
// SAMPLE_BLIND_SPOT — the thing you can't see
// ---------------------------------------------------------------------------

export const SAMPLE_BLIND_SPOT = {
  narrative: "You are so fluent at synthesizing across domains that you sometimes mistake the feeling of integration for the fact of it. When two frameworks click together in your mind, the aesthetic satisfaction can outrun the actual logical validity — and because the result feels inevitable, you don't always check the joints. Your blind spot isn't ignorance; it's premature elegance. The most dangerous moment in your thinking is the one where everything suddenly makes sense.",
}
